"use client";

import {
	useControllableState,
	useDebouncedCallback,
	useDebouncedValue,
} from "@frend-digital/ui/hooks";
import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import { ALGOLIA_PRODUCTS_INDEX, algoliaClient, HITS_PER_PAGE } from "./config";
import {
	useAlgoliaAutoCompleteSuggestions,
	useAlgoliaPopularSearches,
} from "./hooks";

export interface AlgoliaSearchProps {
	value?: string;
	onValueChange?: Dispatch<SetStateAction<string>>;
	children: (args: {
		query: string;
		debouncedQuery?: string;

		onQueryChange: (e: ChangeEvent<HTMLInputElement>) => void;
		products?: AlgoliaProductCard[];
		isLoading: boolean;
		isFetchingNextPage: boolean;
		hasMoreProducts: boolean;
		fetchNextPage: () => void;
		cardRecords?: Record<string, AlgoliaProductCard[]>;
		results: number;
		error: Error | null;
		autoCompleteSuggestions: string[];
		popularSearches: string[];
	}) => ReactNode;
}

export const AlgoliaSearch = ({
	value,
	onValueChange,
	children,
}: AlgoliaSearchProps) => {
	const searchParams = useSearchParams();
	const [undebouncedQuery, setQuery] = useControllableState(
		searchParams.get("q") ?? "",
		value,
		onValueChange,
	);

	const immediate = true;

	const query = useDebouncedValue(undebouncedQuery, 150);

	const {
		data,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		error,
	} = useInfiniteQuery({
		queryKey: [
			"search",
			ALGOLIA_PRODUCTS_INDEX,
			immediate,
			query,
			HITS_PER_PAGE,
		],
		queryFn: async ({ pageParam = 0 }) => {
			const data = await algoliaClient.searchSingleIndex<AlgoliaProductCard>(
				{
					indexName: ALGOLIA_PRODUCTS_INDEX,
					searchParams: {
						hitsPerPage: HITS_PER_PAGE,
						query,
						clickAnalytics: true,
						page: pageParam,
						...searchParams,
					},
				},
				{
					cacheable: true,
				},
			);

			return data;
		},
		getNextPageParam: (lastPage, pages) => {
			const currentPage = pages.length - 1;
			const totalPages = Math.ceil((lastPage?.nbHits ?? 0) / HITS_PER_PAGE);
			return currentPage < totalPages - 1 ? currentPage + 1 : undefined;
		},
		initialPageParam: 0,
		enabled: immediate,
		placeholderData: keepPreviousData,
	});

	const { autoCompleteSuggestions } = useAlgoliaAutoCompleteSuggestions({
		data: data?.pages[0],
		query,
	});

	const { data: popularSearches } = useAlgoliaPopularSearches();

	const handleQueryChange = useDebouncedCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newQuery = e.target.value;

			setQuery(newQuery);
		},
		500,
	);

	const products = data?.pages.flatMap((page) => page.hits) || [];

	const cardRecords: Record<string, AlgoliaProductCard[]> = products.reduce(
		(
			acc: Record<string, AlgoliaProductCard[]>,
			product: AlgoliaProductCard,
		) => {
			if (!product?.productType) return acc;

			if (!acc[product.productType]) {
				acc[product.productType] = [];
			}

			const card = product;
			acc[product.productType].push(card);
			return acc;
		},
		{},
	) as Record<string, AlgoliaProductCard[]>;

	const results = cardRecords
		? Object.values(cardRecords).reduce((acc, cards) => acc + cards.length, 0)
		: 0;

	return children({
		query: undebouncedQuery,
		debouncedQuery: query,
		isLoading,
		isFetchingNextPage,
		onQueryChange: handleQueryChange,
		products,
		hasMoreProducts: hasNextPage && !!query,
		fetchNextPage,
		results,
		cardRecords,
		error,
		autoCompleteSuggestions,
		popularSearches: popularSearches || [],
	});
};
